import { useEffect, useRef, useState } from "react";

interface Props {
  onClose: () => void;
}

const TABS = [
  { id: "call", label: "Llamada en vivo", icon: "ri-phone-line" },
  { id: "dashboard", label: "Dashboard", icon: "ri-bar-chart-2-line" },
  { id: "agenda", label: "Agenda automática", icon: "ri-calendar-check-line" },
  { id: "config", label: "Configuración IA", icon: "ri-settings-3-line" },
];

const CONVERSATION = [
  { role: "ia", text: "Buenos días, Clínica Dental Premium. ¿En qué puedo ayudarle?", delay: 600 },
  { role: "user", text: "Hola, necesito una cita con el Dr. García para esta semana.", delay: 2000 },
  { role: "ia", text: "Por supuesto. ¿Me puede decir su nombre y el motivo de la visita?", delay: 3200 },
  { role: "user", text: "Soy Ana López. Es para una revisión y limpieza dental.", delay: 4800 },
  { role: "ia", text: "Perfecto Ana. Tengo disponibilidad el miércoles a las 10:30h o el jueves a las 17:00h. ¿Cuál le viene mejor?", delay: 6400 },
  { role: "user", text: "El miércoles perfecto.", delay: 8200 },
  { role: "ia", text: "¡Listo! He agendado su cita para el miércoles 22 a las 10:30h con el Dr. García. Le llegará un SMS de confirmación. ¿Necesita algo más?", delay: 9800 },
  { role: "confirm", text: "✓ Cita creada en Google Calendar · SMS enviado · Lead registrado en CRM", delay: 11400 },
];

const METRICS = [
  { label: "Llamadas respondidas", value: 1847, suffix: "", icon: "ri-phone-fill", color: "text-amber-500", pct: 92 },
  { label: "Leads capturados", value: 342, suffix: "", icon: "ri-user-add-fill", color: "text-emerald-500", pct: 68 },
  { label: "Citas agendadas", value: 218, suffix: "", icon: "ri-calendar-fill", color: "text-stone-700", pct: 54 },
  { label: "Coste por lead", value: 4.2, suffix: "€", icon: "ri-money-euro-circle-fill", color: "text-amber-500", pct: 30 },
];

const APPOINTMENTS = [
  { time: "09:00", name: "Carlos Ruiz", type: "Revisión", duration: 60 },
  { time: "10:30", name: "Ana López", type: "Limpieza", duration: 45, highlight: true },
  { time: "12:00", name: "María García", type: "Ortodoncia", duration: 90 },
  { time: "16:00", name: "Juan Martínez", type: "Urgencia", duration: 30 },
  { time: "17:30", name: "Laura Sánchez", type: "Revisión", duration: 60 },
];

function CallTab() {
  const [messages, setMessages] = useState<typeof CONVERSATION>([]);
  const [playing, setPlaying] = useState(false);
  const [done, setDone] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const startDemo = () => {
    setMessages([]);
    setDone(false);
    setPlaying(true);
    timers.current.forEach(clearTimeout);
    timers.current = [];
    CONVERSATION.forEach((msg, i) => {
      const t = setTimeout(() => {
        setMessages((prev) => [...prev, msg]);
        if (i === CONVERSATION.length - 1) setDone(true);
      }, msg.delay);
      timers.current.push(t);
    });
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100">
            <i className="ri-robot-2-line text-amber-600 text-lg" />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-800">Recepcionista IA — Clínica Dental Premium</p>
            <p className="text-xs text-stone-500">Simulación de llamada real · OpenAI Realtime API</p>
          </div>
        </div>
        {!playing ? (
          <button
            onClick={startDemo}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line" />
            Reproducir demo
          </button>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-emerald-600 text-xs font-semibold">En llamada</span>
          </div>
        )}
      </div>

      <div className="flex-1 bg-stone-50 rounded-2xl p-4 overflow-y-auto space-y-3 min-h-[260px]">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-stone-100 mb-3">
              <i className="ri-phone-line text-stone-400 text-2xl" />
            </div>
            <p className="text-stone-500 text-sm">Pulsa &quot;Reproducir demo&quot; para ver cómo Auria gestiona una llamada real</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "confirm" ? (
              <div className="w-full px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-xs font-semibold text-center animate-fadeIn">
                <i className="ri-check-double-line mr-1" />
                {msg.text}
              </div>
            ) : (
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm animate-fadeIn ${
                msg.role === "ia"
                  ? "bg-white border border-stone-200 text-stone-700"
                  : "bg-amber-500 text-white"
              }`}>
                <span className={`text-xs font-bold block mb-0.5 ${msg.role === "ia" ? "text-amber-600" : "text-amber-100"}`}>
                  {msg.role === "ia" ? "Auria IA" : "Cliente"}
                </span>
                {msg.text}
              </div>
            )}
          </div>
        ))}
      </div>

      {done && (
        <div className="mt-3 flex items-center gap-3 text-xs text-stone-500 justify-center animate-fadeIn">
          <span className="flex items-center gap-1"><i className="ri-time-line" /> Duración: 0m 42s</span>
          <span className="flex items-center gap-1 text-emerald-500"><i className="ri-check-line" /> Cita agendada</span>
          <span className="flex items-center gap-1"><i className="ri-global-line" /> ES detectado</span>
        </div>
      )}
    </div>
  );
}

function DashboardTab() {
  const [counts, setCounts] = useState([0, 0, 0, 0]);

  useEffect(() => {
    METRICS.forEach((m, i) => {
      let start = 0;
      const step = m.value / 40;
      const interval = setInterval(() => {
        start = Math.min(start + step, m.value);
        setCounts((prev) => { const n = [...prev]; n[i] = start; return n; });
        if (start >= m.value) clearInterval(interval);
      }, 30);
    });
  }, []);

  const recentCalls = [
    { time: "14:32", caller: "+34 612 345 678", outcome: "Cita agendada", lang: "ES", ok: true },
    { time: "13:15", caller: "+44 7700 900123", outcome: "Lead capturado", lang: "EN", ok: true },
    { time: "11:48", caller: "+34 699 112 233", outcome: "Cita agendada", lang: "ES", ok: true },
    { time: "10:23", caller: "+33 6 12 34 56 78", outcome: "Info enviada", lang: "FR", ok: true },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {METRICS.map((m, i) => (
          <div key={m.label} className="bg-stone-50 border border-stone-100 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${m.icon} ${m.color} text-lg`} />
              <span className="text-xs text-stone-500 leading-tight">{m.label}</span>
            </div>
            <p className="text-2xl font-black text-stone-900">
              {m.suffix === "€" ? `${counts[i].toFixed(2)}${m.suffix}` : Math.round(counts[i]).toLocaleString("es")}
            </p>
            <div className="mt-2 h-1.5 bg-stone-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full transition-all duration-1000" style={{ width: `${m.pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-stone-50 border border-stone-100 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
          <span className="text-xs font-bold text-stone-700 uppercase tracking-wide">Llamadas recientes — Hoy</span>
          <span className="text-xs text-stone-400">Actualizado en tiempo real</span>
        </div>
        <div className="divide-y divide-stone-100">
          {recentCalls.map((c, i) => (
            <div key={i} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs text-stone-400 w-10">{c.time}</span>
                <span className="text-sm text-stone-700 font-mono">{c.caller}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full font-mono">{c.lang}</span>
                <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                  <i className="ri-check-line" />{c.outcome}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AgendaTab() {
  const [newAppt, setNewAppt] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setNewAppt(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const days = ["Lun 19", "Mar 20", "Mié 21", "Jue 22", "Vie 23"];

  return (
    <div className="space-y-4">
      {newAppt && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl animate-fadeIn">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
            <i className="ri-calendar-check-line text-emerald-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-emerald-700">Nueva cita agendada automáticamente</p>
            <p className="text-xs text-emerald-600">Ana López · Mié 21 a las 10:30h · SMS enviado</p>
          </div>
        </div>
      )}

      <div className="bg-stone-50 border border-stone-100 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-5 border-b border-stone-100">
          {days.map((d) => (
            <div key={d} className={`px-3 py-3 text-center text-xs font-bold border-r last:border-r-0 border-stone-100 ${d === "Mié 21" ? "bg-amber-50 text-amber-700" : "text-stone-500"}`}>
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-5 divide-x divide-stone-100 min-h-[200px]">
          {days.map((d) => (
            <div key={d} className="p-2 space-y-1.5">
              {APPOINTMENTS.filter((_, i) => {
                if (d === "Mié 21") return true;
                if (d === "Mar 20") return i < 2;
                if (d === "Jue 22") return i === 1 || i === 3;
                if (d === "Vie 23") return i === 0;
                return false;
              }).map((appt, i) => (
                <div
                  key={i}
                  className={`rounded-lg px-2 py-1.5 text-xs ${
                    appt.highlight && d === "Mié 21"
                      ? "bg-amber-400 text-white font-semibold"
                      : "bg-white border border-stone-200 text-stone-600"
                  }`}
                >
                  <p className="font-bold truncate">{appt.time}</p>
                  <p className="truncate opacity-80">{appt.name}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: "ri-calendar-fill", label: "Citas esta semana", value: "23" },
          { icon: "ri-time-line", label: "Horas liberadas", value: "4.5h" },
          { icon: "ri-spam-2-line", label: "Conflictos resueltos", value: "7" },
        ].map((s) => (
          <div key={s.label} className="bg-stone-50 border border-stone-100 rounded-xl p-3 text-center">
            <i className={`${s.icon} text-amber-500 text-xl mb-1`} />
            <p className="text-xl font-black text-stone-900">{s.value}</p>
            <p className="text-xs text-stone-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigTab() {
  const defaultContent = `NOMBRE DEL NEGOCIO: Clínica Dental Premium
ESPECIALIDAD: Odontología general, ortodoncia y estética dental

HORARIO DE ATENCIÓN:
- Lunes a Viernes: 9:00h - 20:00h
- Sábados: 9:00h - 14:00h
- Festivos y domingos: Cerrado

SERVICIOS Y PRECIOS:
- Revisión y limpieza: 80€ (45 min)
- Ortodoncia invisible: consultar presupuesto (60 min)
- Blanqueamiento dental: 250€ (90 min)
- Urgencias: 120€ (30 min)

DOCTORES DISPONIBLES:
- Dr. García → Lunes, Miércoles y Viernes
- Dra. Martínez → Martes y Jueves
- Dr. López → Sábados

PROTOCOLO DE URGENCIAS:
Si el paciente tiene dolor agudo → transfiere al +34 612 000 000
Si es fuera de horario → agenda para primera hora disponible

CANCELACIONES:
Recordar que se requiere aviso de 24h para cancelar sin cargo.`;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
        <i className="ri-lightbulb-line text-amber-600 text-lg flex-shrink-0" />
        <p className="text-xs text-amber-700 leading-relaxed">
          <strong>Canvas Memory</strong> — Escribe aquí todo lo que tu IA debe saber sobre tu negocio. Cuanto más detallado, más precisa será la atención.
        </p>
      </div>
      <div className="relative">
        <textarea
          defaultValue={defaultContent}
          className="w-full h-48 bg-stone-50 border border-stone-200 rounded-2xl p-4 text-xs text-stone-700 font-mono leading-relaxed outline-none focus:border-amber-400 resize-none"
        />
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <span className="text-xs text-stone-400">Auto-guardado</span>
          <i className="ri-check-line text-emerald-500 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: "ri-global-line", label: "Idioma principal", value: "Español (ES)" },
          { icon: "ri-phone-line", label: "Número asignado", value: "+34 900 123 456" },
          { icon: "ri-calendar-line", label: "Calendar conectado", value: "Google Calendar ✓" },
          { icon: "ri-brain-line", label: "Modelo IA activo", value: "GPT-4o Realtime" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-3 py-2.5">
            <i className={`${item.icon} text-amber-500 text-base flex-shrink-0`} />
            <div>
              <p className="text-xs text-stone-400">{item.label}</p>
              <p className="text-xs font-bold text-stone-700">{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DemoModal({ onClose }: Props) {
  const [activeTab, setActiveTab] = useState("call");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/fe353479-d614-4f61-bfb2-b1338c679355.png"
              alt="Auria"
              className="h-7 w-auto"
            />
            <div>
              <p className="text-sm font-bold text-stone-900">Demo Interactiva</p>
              <p className="text-xs text-stone-400">Explora Auria sin registrarte</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-stone-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100 px-6 flex-shrink-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-3.5 text-xs font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap mr-1 ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-600"
                  : "border-transparent text-stone-400 hover:text-stone-600"
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "call" && <CallTab />}
          {activeTab === "dashboard" && <DashboardTab />}
          {activeTab === "agenda" && <AgendaTab />}
          {activeTab === "config" && <ConfigTab />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-stone-400">
            <i className="ri-lock-2-line mr-1" />
            Datos simulados · Sin tarjeta de crédito
          </p>
          <a
            href="#precios"
            onClick={onClose}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-rocket-line" />
            Empezar con Auria
          </a>
        </div>
      </div>
    </div>
  );
}
